// src/modules/helpers/item.js
import React, { useState, useEffect } from "react";
import { IonIcon } from "@ionic/react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { reload, chevronDown, chevronForward, trash } from "ionicons/icons";
import ErrorModal from "./ErrorModal";
import { useLanguage } from "./LanguageContext";
import "../../css/TableListItem.css";

export default function TableListItem({
  Item: initialItem,
  onItemChange,
  onDelete,
  readOnly = false,
  checked = false,
  onCheckedChange
}) {
  const { t } = useLanguage();

  function parseStringToArray(val) {
    if (Array.isArray(val)) return val.map(Number);
    if (typeof val === "string") {
      try {
        const arr = JSON.parse(val);
        if (Array.isArray(arr)) return arr.map(Number);
      } catch {}
      return [...String(val).matchAll(/-?\d+(\.\d+)?/g)].map((m) =>
        Number(m[0])
      );
    }
    return [0, 0, 0];
  }

  function parseBracketString(str) {
    if (!str) return ["", "0", "0"];
    const parts = str
      .slice(1, -1)
      .split(",")
      .map((s) => s.trim());
    return parts.length === 3 ? parts : ["", "0", "0"];
  }

  const [item, setItem] = useState(() => {
    const sizeArr = parseStringToArray(initialItem.size);
    const [cantType, longCant, shortCant] = parseBracketString(
      initialItem.kant
    );
    return {
      ...initialItem,
      sizeArr,
      cantType,
      longCant,
      shortCant,
      millingId: initialItem.milling?.millingId ?? initialItem.millingId ?? null
    };
  });
  const [showError, setShowError] = useState(false);
  const [collapsed, setCollapsed] = useState(true);

  useEffect(() => {
    const sizeArr = parseStringToArray(initialItem.size);
    const [ct, lc, sc] = parseBracketString(initialItem.kant);
    setItem({
      ...initialItem,
      sizeArr,
      cantType: ct,
      longCant: lc,
      shortCant: sc,
      millingId: initialItem.milling?.millingId ?? initialItem.millingId ?? null
    });
  }, [initialItem]);

  const validateForm = () => {
    if (
      item.sizeArr.length !== 3 ||
      item.sizeArr.some((v) => isNaN(v) || v <= 0)
    )
      return false;
    if (item.qty === "" || isNaN(item.qty) || item.qty <= 0) return false;
    if (isNaN(Number(item.longCant)) || isNaN(Number(item.shortCant)))
      return false;
    return true;
  };

  const commitIfValid = () => {
    if (readOnly) return;
    if (validateForm()) {
      setShowError(false);
      onItemChange(item);
    } else setShowError(true);
  };

  const handleChange = (field, value) => {
    if (readOnly) return;
    let updated = { ...item };
    if (field === "sizeArr") {
      const nums = value.map((v) => {
        const n = Number(v);
        return Number.isFinite(n) ? n : 0;
      });
      updated.sizeArr = nums;
      updated.size = JSON.stringify(nums);
    } else {
      updated[field] = value;
    }
    if (["cantType", "longCant", "shortCant"].includes(field)) {
      updated.kant = `[${updated.cantType},${updated.longCant},${updated.shortCant}]`;
    }
    setItem(updated);
  };

  const handleRotate = () => {
    const updated = { ...item, rotable: !item.rotable };
    setItem(updated);
    if (validateForm()) {
      setShowError(false);
      onItemChange(updated);
    }
  };

  const handleDelete = () => {
    if (readOnly) return;
    if (window.confirm(t("itemDeleteConfirm"))) onDelete();
  };

  const ROW_H = "1.75rem";
  const BORDER = "1px solid #e0e0e0";
  const CB_W = "1.4rem";
  const EYE_W = "1.6rem";
  const SWATCH_W = "1.6rem";
  const W_W = "3.6rem";
  const H_W = "3.6rem";
  const DB_W = "2.8rem";
  const CHEV_W = "1.6rem";

  const cell = (extra = {}) => ({
    height: ROW_H,
    borderLeft: BORDER,
    background: "transparent",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    ...extra
  });

  const cellInput = {
    height: "100%",
    width: "100%",
    border: "none",
    background: "transparent",
    outline: "none",
    fontSize: "0.82rem",
    padding: "0 4px",
    color: "inherit",
    ...(readOnly ? { pointerEvents: "none", color: "#888" } : {})
  };

  const cellSelect = {
    height: "100%",
    width: "100%",
    border: "none",
    background: "transparent",
    outline: "none",
    fontSize: "0.82rem",
    padding: "0 2px",
    cursor: readOnly ? "default" : "pointer",
    ...(readOnly ? { color: "#888" } : {})
  };

  const RotateBtn = () => (
    <OverlayTrigger
      placement="top"
      overlay={
        <Tooltip>
          {item.rotable ? t("itemRotatable") : t("itemNotRotatable")}
        </Tooltip>
      }
    >
      <span
        role="button"
        tabIndex={0}
        onClick={handleRotate}
        className="table-list-item__rotate-button"
      >
        <IonIcon
          icon={reload}
          className={[
            "table-list-item__rotate-icon",
            item.rotable
              ? "table-list-item__rotate-icon--active"
              : "table-list-item__rotate-icon--inactive"
          ]
            .filter(Boolean)
            .join(" ")}
        />

        {!item.rotable && <span className="table-list-item__rotate-slash" />}
      </span>
    </OverlayTrigger>
  );

  return (
    <div
      className={["table-list-item", readOnly && "table-list-item--readonly"]
        .filter(Boolean)
        .join(" ")}
    >
      <div
        className={[
          "table-list-item__row",
          collapsed && "table-list-item__row--collapsed"
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <div className="table-list-item__chevron-cell">
          <IonIcon
            icon={collapsed ? chevronForward : chevronDown}
            className="table-list-item__chevron"
            onClick={() => setCollapsed((v) => !v)}
          />
        </div>

        <div
          className="table-list-item__checkbox-cell"
          onClick={(e) => {
            e.stopPropagation();
            if (typeof onCheckedChange === "function") {
              onCheckedChange(!checked, e);
            }
          }}
        >
          <input
            type="checkbox"
            checked={!!checked}
            onChange={() => {}}
            onClick={(e) => e.stopPropagation()}
            className="table-list-item__checkbox"
          />
        </div>

        <div className="table-list-item__cell table-list-item__cell--width">
          <input
            className="table-list-item__input table-list-item__input--right"
            value={item.sizeArr[0]}
            onChange={(e) =>
              handleChange("sizeArr", [
                e.target.value,
                item.sizeArr[1],
                item.sizeArr[2]
              ])
            }
            onBlur={commitIfValid}
            disabled={readOnly}
          />
        </div>

        <div className="table-list-item__cell table-list-item__cell--height">
          <input
            className="table-list-item__input table-list-item__input--right"
            value={item.sizeArr[1]}
            onChange={(e) =>
              handleChange("sizeArr", [
                item.sizeArr[0],
                e.target.value,
                item.sizeArr[2]
              ])
            }
            onBlur={commitIfValid}
            disabled={readOnly}
          />
        </div>

        <div className="table-list-item__cell table-list-item__cell--qty">
          <input
            className="table-list-item__input table-list-item__input--center"
            value={item.qty}
            onChange={(e) => handleChange("qty", e.target.value)}
            onBlur={commitIfValid}
            disabled={readOnly}
          />
        </div>

        <div className="table-list-item__cell table-list-item__cell--name">
          <input
            className="table-list-item__input"
            placeholder={t("itemNamePlaceholder")}
            value={item.name ? t(item.name) : ""}
            onChange={(e) => handleChange("name", e.target.value)}
            onBlur={commitIfValid}
            disabled={readOnly}
          />
        </div>
      </div>

      {!collapsed && (
        <div className="table-list-item__row table-list-item__row--details">
          <div className="table-list-item__cell table-list-item__cell--action">
            {!readOnly && (
              <IonIcon
                icon={trash}
                className="table-list-item__delete-icon"
                onClick={handleDelete}
              />
            )}
          </div>

          <div className="table-list-item__cell table-list-item__cell--swatch">
            <RotateBtn />
          </div>

          <div className="table-list-item__cell table-list-item__cell--width">
            <select
              className="table-list-item__select"
              value={item.longCant}
              onChange={(e) => handleChange("longCant", e.target.value)}
              onBlur={commitIfValid}
              disabled={readOnly}
            >
              <option value="0">0</option>
              <option value="1">1</option>
              <option value="2">2</option>
            </select>
          </div>

          <div className="table-list-item__cell table-list-item__cell--height">
            <select
              className="table-list-item__select"
              value={item.shortCant}
              onChange={(e) => handleChange("shortCant", e.target.value)}
              onBlur={commitIfValid}
              disabled={readOnly}
            >
              <option value="0">0</option>
              <option value="1">1</option>
              <option value="2">2</option>
            </select>
          </div>

          <div className="table-list-item__cell table-list-item__cell--qty">
            <select
              className="table-list-item__select"
              value={item.cantType}
              onChange={(e) => handleChange("cantType", e.target.value)}
              onBlur={commitIfValid}
              disabled={readOnly}
            >
              <option value="-">-</option>
              <option value="04">04</option>
              <option value="2">2</option>
              <option value="42">42</option>
              <option value="1">1</option>
            </select>
          </div>

          <div className="table-list-item__cell table-list-item__cell--name">
            <input
              className="table-list-item__input"
              placeholder={t("itemDetailsPlaceholder")}
              value={item.details || ""}
              onChange={(e) => handleChange("details", e.target.value)}
              onBlur={commitIfValid}
              disabled={readOnly}
            />
          </div>

          <div className="table-list-item__cell table-list-item__cell--chevron-spacer" />
        </div>
      )}

      {showError && (
        <ErrorModal
          error="Please fill out all fields correctly."
          onClose={() => setShowError(false)}
        />
      )}
    </div>
  );
}
